/**
 * ListeMailiYaz — bir posta listesine bir posta yazıp göndermek (backlog #585, backend POST /email/lists/{id}/send).
 *
 * Konu ve gövde burada yazılır ya da bir serbest şablondan başlatılır (şablonun Türkçe ve İngilizcesi alanlara dolar, sonra düzenlenir). İngilizce sürüm isteğe
 * bağlıdır: bir alıcı İngilizceyi YALNIZ konu ve gövde ikisi de yazılmışsa VE kullanıcının dili İngilizceyse okur; aksi halde Türkçe gider.
 * Gönderen: sistemin varsayılan "no-reply" hesabı (her personel; sistemle ilgili postalar, kişisel değil) ya da personelin kendi hesabı (liste GET /email/send/options'tan gelir).
 * İki adım: önizleme kaç kişiye gideceğini (adresi olmayan, aynı adres, güvenli mod) söyler; gönderim, görülen sayının onaylanmasıyla başlar ve bir İŞ olarak çalışır
 * (aynı anda tek iş; ilerleme izlenir, durdurulabilir, art arda 5 hatada kendiliğinden durur). Her postanın sonucu E-posta Kayıtları'ndadır.
 *
 * `isOpen` kapalıyken hiçbir şey çizilmez: her açılışta form sıfırdan başlar.
 */
import React, { useEffect, useState } from "react";
import { Alert, Button, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Progress, Spinner } from "reactstrap";
import { useTranslation } from "react-i18next";
import PostaEditoru from "./PostaEditoru";
import { getFreeTemplate, getSendOptions, listFreeTemplates } from "../../../../../services/emailAyarService";
import { cancelListSendJob, getListSendJob, previewListSend, sendToMailList } from "../../../../../services/postaListeleriService";

const IZLEME_MS = 1500;

/** Editörün HTML'inde okunur bir metin var mı (boş editör `<p>&nbsp;</p>` verir). */
export function metinVar(html) {
    return (html || "").replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim() !== "";
}

const YazForm = ({ liste, toggle, onFinished }) => {
    const { t } = useTranslation("moduller");
    const [secenekler, setSecenekler] = useState(null);
    const [sablonlar, setSablonlar] = useState([]);
    const [yukleniyor, setYukleniyor] = useState(true);
    const [yuklemeHatasi, setYuklemeHatasi] = useState(null);
    const [gonderenId, setGonderenId] = useState("");
    const [sablonId, setSablonId] = useState("");
    const [konu, setKonu] = useState("");
    const [govde, setGovde] = useState("");
    const [konuEn, setKonuEn] = useState("");
    const [govdeEn, setGovdeEn] = useState("");
    const [ingilizceAcik, setIngilizceAcik] = useState(false);
    const [onizleme, setOnizleme] = useState(null);            // { anahtar, veri | hata }
    const [onay, setOnay] = useState(false);
    const [gonderiyor, setGonderiyor] = useState(false);
    const [is, setIs] = useState(null);                         // the job's progress
    const [hata, setHata] = useState(null);
    const id = liste.mail_list_id;

    useEffect(() => {
        let ignore = false;
        async function run() {
            try {
                const [s, l] = await Promise.all([getSendOptions(), listFreeTemplates()]);
                if (ignore) return;
                setSecenekler(s);
                setSablonlar(l);
                const ilk = (s.senders || []).find((x) => x.kind === "DEFAULT") || (s.senders || []).find((x) => x.kind === "OWN") || (s.senders || [])[0];
                if (ilk) setGonderenId(String(ilk.system_email_id));
            } catch (e) {
                if (!ignore) setYuklemeHatasi(e.message);
            } finally {
                if (!ignore) setYukleniyor(false);
            }
        }
        run();
        return () => { ignore = true; };
    }, []);

    // Önizleme: gönderen değişince (ve açılışta) — sayılar o göndericinin kurallarına göre.
    // 2026-10-30 (Bilge): a SYSTEM template in the picker turns the form into "send that system mail to every member" (each with a new password link, in their
    // own language, from the default sender; no text to write). The preview then counts for that mail.
    const secili = sablonlar.find((s) => String(s.email_template_id) === String(sablonId));
    const sistemKey = secili?.template_scope === "SYSTEM" ? secili.template_key : null;
    // WAS (#585): const onizlemeAnahtari = gonderenId ? `${id}:${gonderenId}` : null;
    const onizlemeAnahtari = gonderenId ? `${id}:${gonderenId}:${sistemKey || ""}` : null;
    useEffect(() => {
        if (!onizlemeAnahtari) return undefined;
        const [, gonderen, sistem] = onizlemeAnahtari.split(":");
        let ignore = false;
        async function run() {
            try {
                const veri = sistem ? await previewListSend(id, Number(gonderen), sistem) : await previewListSend(id, Number(gonderen));
                if (!ignore) setOnizleme({ anahtar: onizlemeAnahtari, veri });
            } catch (e) {
                if (!ignore) setOnizleme({ anahtar: onizlemeAnahtari, hata: e.message });
            }
        }
        run();
        return () => { ignore = true; };
    }, [id, onizlemeAnahtari]);
    const buOnizleme = onizleme && onizleme.anahtar === onizlemeAnahtari ? onizleme : null;
    const sayi = buOnizleme?.veri?.will_send ?? 0;

    // İş çalışırken ilerlemesini izle.
    useEffect(() => {
        if (!is || is.state !== "RUNNING") return undefined;
        let ignore = false;
        const zamanlayici = setTimeout(async () => {
            try {
                const j = await getListSendJob(is.jobId);
                if (!ignore) setIs(j);
            } catch (e) {
                if (!ignore) setHata(e.message);
            }
        }, IZLEME_MS);
        return () => { ignore = true; clearTimeout(zamanlayici); };
    }, [is]);

    // İş bitince liste (ve kayıtlar) tazelensin.
    const bitti = Boolean(is) && is.state !== "RUNNING";
    useEffect(() => {
        if (bitti && onFinished) onFinished();
    }, [bitti]);   // eslint-disable-line react-hooks/exhaustive-deps

    async function sablondanBasla(yeniId) {
        setSablonId(yeniId);
        setOnay(false);
        if (!yeniId) return;
        setHata(null);
        // a system mail has no text to load: the form only asks who it goes to
        if (sablonlar.some((s) => String(s.email_template_id) === String(yeniId) && s.template_scope === "SYSTEM")) return;
        try {
            const s = await getFreeTemplate(Number(yeniId));
            setKonu(s.email_template_title || "");
            setGovde(s.email_template_content || "");
            setKonuEn(s.email_template_title_en || "");
            setGovdeEn(s.email_template_content_en || "");
            setIngilizceAcik(Boolean(s.email_template_title_en || s.email_template_content_en));
        } catch (e) {
            setHata(e.message);
        }
    }

    // WAS (#585): const gonderebilir = !gonderiyor && !is && Boolean(gonderenId) && konu.trim() !== "" && metinVar(govde) && sayi > 0 && !buOnizleme?.veri?.too_many && onay;
    const yazilmis = sistemKey ? true : konu.trim() !== "" && metinVar(govde);
    const gonderebilir = !gonderiyor && !is && Boolean(gonderenId) && yazilmis && sayi > 0 && !buOnizleme?.veri?.too_many && onay;

    async function gonder() {
        setGonderiyor(true);
        setHata(null);
        try {
            setIs(await sendToMailList(id, sistemKey ? { systemMail: sistemKey, confirmedCount: sayi } : {
                senderId: Number(gonderenId), subject: konu.trim(), html: govde,
                subjectEn: konuEn.trim(), htmlEn: metinVar(govdeEn) ? govdeEn : "", confirmedCount: sayi,
            }));
        } catch (e) {
            setHata(e.message);
        } finally {
            setGonderiyor(false);
        }
    }

    async function durdur() {
        try {
            setIs(await cancelListSendJob(is.jobId));
        } catch (e) {
            setHata(e.message);
        }
    }

    const gonderenler = secenekler?.senders || [];
    const tur = (kind) => t(kind === "OWN" ? "emailsGonder.turBenim" : kind === "DEFAULT" ? "emailsGonder.turVarsayilan" : "emailsGonder.turGenel");
    const ozet = buOnizleme?.veri;

    return (
        <Modal isOpen toggle={toggle} size="xl" scrollable backdrop="static">
            <ModalHeader toggle={toggle}>{t("emailsListe.postaYazBaslik", { ad: liste.mail_list_name })}</ModalHeader>
            <ModalBody>
                {yukleniyor && <div className="text-center py-4"><Spinner size="sm" /></div>}
                {yuklemeHatasi && <Alert color="danger">{yuklemeHatasi}</Alert>}

                {!yukleniyor && !yuklemeHatasi && is && (
                    <div>
                        <Alert color={is.state === "RUNNING" ? "info" : is.state === "DONE" ? "success" : "warning"}>
                            {t(`emailsListe.durum_${is.state}`)}
                            {is.stoppedReason ? ` — ${is.stoppedReason}` : ""}
                        </Alert>
                        <Progress value={is.total ? Math.round((is.processed * 100) / is.total) : 0} className="mb-2" />
                        <p className="mb-2">
                            {t("emailsListe.ilerleme", { islenen: is.processed, toplam: is.total })}
                            {" · "}{t("emailsListe.gonderildi", { n: is.sent })}
                            {" · "}{t("emailsListe.hatali", { n: is.failed })}
                            {" · "}{t("emailsListe.atlandi", { n: is.skipped })}
                            {" · "}{t("emailsListe.engellendi", { n: is.blocked })}
                        </p>
                        {is.firstFailures?.length > 0 && (
                            <ul className="small text-danger">
                                {is.firstFailures.map((f, i) => <li key={`${f.userId}-${i}`}>#{f.userId} — {f.reason}</li>)}
                            </ul>
                        )}
                        <p className="text-muted small mb-0">{t("emailsListe.sonucKayitlarda")}</p>
                    </div>
                )}

                {!yukleniyor && !yuklemeHatasi && !is && (
                    <div>
                        <div className="row g-3 mb-3">
                            <div className="col-md-6">
                                <Label className="form-label" htmlFor="yaz-gonderen">{t("emailsGonder.gonderen")}</Label>
                                {gonderenler.length > 0 ? (
                                    <Input id="yaz-gonderen" type="select" value={gonderenId} onChange={(e) => { setGonderenId(e.target.value); setOnay(false); }}>
                                        {gonderenler.map((s) => (
                                            <option key={s.system_email_id} value={s.system_email_id}>{s.system_email_title} [{s.system_email_value}] · {tur(s.kind)}</option>
                                        ))}
                                    </Input>
                                ) : <Alert color="warning" className="mb-0">{t("emailsListe.gonderenYok")}</Alert>}
                            </div>
                            <div className="col-md-6">
                                <Label className="form-label" htmlFor="yaz-sablon">{t("emailsListe.sablondanBasla")}</Label>
                                <Input id="yaz-sablon" type="select" value={sablonId} onChange={(e) => sablondanBasla(e.target.value)}>
                                    <option value="">{t("emailsListe.sablonsuz")}</option>
                                    {sablonlar.map((s) => (
                                        <option key={s.email_template_id} value={s.email_template_id}>
                                            {s.template_scope === "SYSTEM" ? `${s.email_template_title} · ${t("emailsGonder.sistemEtiketi")}` : s.email_template_title}
                                        </option>
                                    ))}
                                </Input>
                            </div>
                        </div>

                        {sistemKey ? <Alert color="info" className="py-2">{t("emailsListe.sistemNotu")}</Alert> : (<>
                        <div className="mb-3">
                            <Label className="form-label" htmlFor="yaz-konu">{t("emailsGonder.konu")}</Label>
                            <Input id="yaz-konu" value={konu} maxLength={255} onChange={(e) => setKonu(e.target.value)} />
                        </div>
                        <div className="mb-3">
                            <Label className="form-label">{t("emailsListe.metin")}</Label>
                            <PostaEditoru value={govde} onChange={setGovde} />
                        </div>

                        <div className="form-check mb-2">
                            <Input id="yaz-ingilizce" type="checkbox" className="form-check-input" checked={ingilizceAcik} onChange={(e) => setIngilizceAcik(e.target.checked)} />
                            <Label className="form-check-label" htmlFor="yaz-ingilizce">{t("emailsListe.ingilizceSurum")}</Label>
                        </div>
                        {ingilizceAcik && (
                            <div className="border rounded p-3 mb-3">
                                <p className="small text-muted">{t("emailsListe.ingilizceKurali")}</p>
                                <div className="mb-3">
                                    <Label className="form-label" htmlFor="yaz-konu-en">{t("emailsListe.konuIngilizce")}</Label>
                                    <Input id="yaz-konu-en" value={konuEn} maxLength={255} onChange={(e) => setKonuEn(e.target.value)} />
                                </div>
                                <Label className="form-label">{t("emailsListe.metinIngilizce")}</Label>
                                <PostaEditoru value={govdeEn} onChange={setGovdeEn} />
                            </div>
                        )}

                        </>)}

                        <div className="border rounded p-3">
                            {!buOnizleme && gonderenId && <div className="text-muted small"><Spinner size="sm" /> {t("emailsListe.sayiniyor")}</div>}
                            {buOnizleme?.hata && <Alert color="danger" className="mb-0">{buOnizleme.hata}</Alert>}
                            {ozet && (
                                <div>
                                    <p className="mb-1"><strong>{t("emailsListe.gidecek", { n: ozet.will_send })}</strong>{" "}
                                        <span className="text-muted">{t("emailsListe.gonderenAdresi", { adres: ozet.sender })}</span></p>
                                    <p className="small text-muted mb-1">
                                        {t("emailsListe.uyeSayisi", { n: ozet.members })}
                                        {ozet.without_address > 0 && ` · ${t("emailsListe.adresiYok", { n: ozet.without_address })}`}
                                        {ozet.bad_address > 0 && ` · ${t("emailsListe.adresBozuk", { n: ozet.bad_address })}`}
                                        {ozet.duplicate_address > 0 && ` · ${t("emailsListe.ayniAdres", { n: ozet.duplicate_address })}`}
                                        {ozet.blocked_by_safe_mode > 0 && ` · ${t("emailsListe.guvenliMod", { n: ozet.blocked_by_safe_mode })}`}
                                    </p>
                                    {ozet.blocked_by_safe_mode > 0 && <Alert color="warning" className="py-2 mb-1">{t("emailsListe.guvenliModAciklama", { neden: ozet.blocked_reason })}</Alert>}
                                    {ozet.too_many && <Alert color="danger" className="py-2 mb-1">{t("emailsListe.cokKisi", { n: ozet.will_send, en: ozet.max_per_job })}</Alert>}
                                    {ozet.running_job_id && <Alert color="warning" className="py-2 mb-1">{t("emailsListe.baskaIsVar")}</Alert>}
                                    <div className="form-check mt-2">
                                        <Input id="yaz-onay" type="checkbox" className="form-check-input" checked={onay} disabled={ozet.will_send === 0} onChange={(e) => setOnay(e.target.checked)} />
                                        <Label className="form-check-label" htmlFor="yaz-onay">{t("emailsListe.onay", { n: ozet.will_send })}</Label>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}
                {hata && <Alert color="danger" className="mt-3 mb-0">{hata}</Alert>}
            </ModalBody>
            <ModalFooter>
                {!is && (
                    <Button color="primary" disabled={!gonderebilir} onClick={gonder}>
                        {gonderiyor ? <Spinner size="sm" /> : t("emailsListe.gonderN", { n: sayi })}
                    </Button>
                )}
                {is && is.state === "RUNNING" && <Button color="danger" outline onClick={durdur}>{t("emailsListe.durdur")}</Button>}
                <Button color="light" onClick={toggle}>{is ? t("emailsGonder.kapat") : t("common:iptal")}</Button>
            </ModalFooter>
        </Modal>
    );
};

const ListeMailiYaz = ({ isOpen, liste, toggle, onFinished }) => (isOpen && liste ? <YazForm liste={liste} toggle={toggle} onFinished={onFinished} /> : null);

export default ListeMailiYaz;
